import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, orderBy, getDocs } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const ESTADOS = {
  borrador: { label: 'Borrador', color: '#AEB6C4', bg: 'rgba(174,182,196,0.15)' },
  enviado: { label: 'Enviado', color: '#00B8FF', bg: 'rgba(0,184,255,0.15)' },
  aceptado: { label: 'Aceptado', color: '#00D084', bg: 'rgba(0,208,132,0.15)' },
  rechazado: { label: 'Rechazado', color: '#FF4757', bg: 'rgba(255,71,87,0.15)' },
};

export default function Presupuestos() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [pacientesDB, setPacientesDB] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [nuevo, setNuevo] = useState({
    pacienteNombre: '', pacienteDni: '', pacienteEmail: '', pacienteTelefono: '',
    incluirEstudios: false, incluirTratamientos: false, incluirCirugias: false,
    detalle: '',
    montoContadoARS: '', montoContadoUSD: '',
    montoFinanciadoARS: '', montoFinanciadoUSD: '',
    detalleFinanciacion: '',
    observaciones: ''
  });

  const canCreate = ['gestion_general', 'recepcion', 'administracion', 'medico'].includes(userRole);
  const canChangeState = userRole === 'recepcion' || ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'presupuestos'), where('clinicaId', '==', clinicaId), orderBy('creadoEn', 'desc'));
    const unsub = onSnapshot(q, snap => { setPresupuestos(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return unsub;
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return;
    getDocs(query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId), orderBy('apellido')))
      .then(snap => setPacientesDB(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [clinicaId]);

  async function crearPresupuesto() {
    if (!nuevo.pacienteNombre) return;
    await addDoc(collection(db, 'presupuestos'), {
      ...nuevo, estado: 'borrador', clinicaId,
      creadoPor: currentUser?.email, creadoEn: new Date(),
    });
    await registrarAccion(currentUser?.email, userRole, 'Presupuestos', 'Crear presupuesto', nuevo.pacienteNombre, clinicaId);
    setModalNuevo(false);
    setNuevo({ pacienteNombre: '', pacienteDni: '', pacienteEmail: '', pacienteTelefono: '', incluirEstudios: false, incluirTratamientos: false, incluirCirugias: false, detalle: '', montoContadoARS: '', montoContadoUSD: '', montoFinanciadoARS: '', montoFinanciadoUSD: '', detalleFinanciacion: '', observaciones: '' });
  }

  const conceptos = p => [p.incluirEstudios && 'Estudios', p.incluirTratamientos && 'Tratamientos', p.incluirCirugias && 'Cirugías'].filter(Boolean).join(' · ') || '—';

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Presupuestos" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>
        {canCreate && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button onClick={() => setModalNuevo(true)} style={btnPrimaryStyle}>+ Nuevo presupuesto</button>
          </div>
        )}
        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : presupuestos.length === 0
            ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px', fontSize: '13px' }}>No hay presupuestos registrados</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {presupuestos.map(p => {
                  const estado = ESTADOS[p.estado] || { label: p.estado, color: theme.secondary, bg: theme.bgCard };
                  const fecha = p.creadoEn?.toDate ? p.creadoEn.toDate().toLocaleDateString('es-AR') : '—';
                  return (
                    <div key={p.id} onClick={() => navigate(`/presupuestos/${p.id}`)} style={{ background: theme.bgCard, borderRadius: '10px', padding: '14px 18px', border: `0.5px solid ${theme.border}`, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
                      onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
                    >
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{p.pacienteNombre}</div>
                        <div style={{ fontSize: '12px', color: theme.secondary }}>{conceptos(p)} · {fecha}</div>
                        {p.detalle && <div style={{ fontSize: '11px', color: theme.secondary, marginTop: '2px' }}>{p.detalle.substring(0, 60)}{p.detalle.length > 60 ? '...' : ''}</div>}
                      </div>
                      <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '99px', fontWeight: '500', background: estado.bg, color: estado.color, flexShrink: 0 }}>
                        {estado.label}
                      </span>
                    </div>
                  );
                })}
              </div>
        }
      </div>

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nuevo presupuesto</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              <div style={{ position: 'relative' }}>
                <label style={labelStyle}>PACIENTE *</label>
                <input value={nuevo.pacienteNombre} onChange={e => { const v = e.target.value; setNuevo({...nuevo, pacienteNombre: v}); if (v.length >= 2) setSugerencias(pacientesDB.filter(p => `${p.apellido} ${p.nombre}`.toLowerCase().includes(v.toLowerCase()) || p.dni?.includes(v)).slice(0,5)); else setSugerencias([]); }} placeholder="Apellido, nombre o DNI..." style={inputStyle} />
                {sugerencias.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: theme.bgModal, border: `0.5px solid ${theme.border}`, borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', overflow: 'hidden', marginTop: '2px' }}>
                    {sugerencias.map(p => (
                      <div key={p.id} onClick={() => { setNuevo({...nuevo, pacienteNombre: `${p.apellido}, ${p.nombre}`, pacienteDni: p.dni || '', pacienteEmail: p.email || '', pacienteTelefono: p.telefono || ''}); setSugerencias([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `0.5px solid ${theme.border}` }} onMouseEnter={e => e.currentTarget.style.background = theme.bgCard} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{p.apellido}, {p.nombre}</div>
                        <div style={{ fontSize: '11px', color: theme.secondary }}>DNI {p.dni} · {p.email}</div>
                      </div>
                    ))}
                    <div onClick={() => setSugerencias([])} style={{ padding: '8px 14px', fontSize: '12px', color: theme.secondary, cursor: 'pointer', textAlign: 'center' }}>Continuar con nombre nuevo</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>DNI</label><input value={nuevo.pacienteDni} onChange={e => setNuevo({...nuevo, pacienteDni: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>EMAIL</label><input value={nuevo.pacienteEmail} onChange={e => setNuevo({...nuevo, pacienteEmail: e.target.value})} style={inputStyle} /></div>
              </div>

              <div>
                <label style={labelStyle}>CONCEPTOS INCLUIDOS</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[['incluirEstudios', 'Estudios diagnósticos'], ['incluirTratamientos', 'Tratamientos'], ['incluirCirugias', 'Cirugías']].map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setNuevo({...nuevo, [key]: !nuevo[key]})} style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '99px', cursor: 'pointer', border: nuevo[key] ? 'none' : `0.5px solid ${theme.borderInput}`, background: nuevo[key] ? theme.accent : 'transparent', color: nuevo[key] ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: nuevo[key] ? '600' : '400' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div><label style={labelStyle}>DETALLE DEL PRESUPUESTO</label><textarea value={nuevo.detalle} onChange={e => setNuevo({...nuevo, detalle: e.target.value})} rows={3} placeholder="Describí el procedimiento, estudios o tratamiento incluido..." style={{ ...inputStyle, resize: 'vertical' }} /></div>

              <div style={{ borderTop: `0.5px solid ${theme.border}`, paddingTop: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: theme.accent, marginBottom: '12px', letterSpacing: '1px' }}>OPCIONES DE PRECIO</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><label style={labelStyle}>CONTADO ARS</label><input type="number" value={nuevo.montoContadoARS} onChange={e => setNuevo({...nuevo, montoContadoARS: e.target.value})} placeholder="0.00" style={inputStyle} /></div>
                  <div><label style={labelStyle}>CONTADO USD</label><input type="number" value={nuevo.montoContadoUSD} onChange={e => setNuevo({...nuevo, montoContadoUSD: e.target.value})} placeholder="0.00" style={inputStyle} /></div>
                  <div><label style={labelStyle}>FINANCIADO ARS</label><input type="number" value={nuevo.montoFinanciadoARS} onChange={e => setNuevo({...nuevo, montoFinanciadoARS: e.target.value})} placeholder="0.00" style={inputStyle} /></div>
                  <div><label style={labelStyle}>FINANCIADO USD</label><input type="number" value={nuevo.montoFinanciadoUSD} onChange={e => setNuevo({...nuevo, montoFinanciadoUSD: e.target.value})} placeholder="0.00" style={inputStyle} /></div>
                </div>
                <div style={{ marginTop: '12px' }}><label style={labelStyle}>DETALLE DE FINANCIACIÓN</label><textarea value={nuevo.detalleFinanciacion} onChange={e => setNuevo({...nuevo, detalleFinanciacion: e.target.value})} rows={2} placeholder="Ej: 12 cuotas sin interés, primer cuota a 30 días..." style={{ ...inputStyle, resize: 'vertical' }} /></div>
              </div>

              <div><label style={labelStyle}>OBSERVACIONES</label><textarea value={nuevo.observaciones} onChange={e => setNuevo({...nuevo, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearPresupuesto} style={btnPrimaryStyle}>Guardar presupuesto</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}